// The English legal, about and report pages.
//
// `legalBinding` is the one key that has no German counterpart worth having:
// the German page is the original and needs no disclaimer, this one says out
// loud that it is a courtesy translation. Without that line a translated
// privacy notice reads as authoritative, which is worse than not offering one.
//
// The German statute names stay German — § 5 DDG and § 18 Abs. 2 MStV are the
// names of German laws, and translating them would make them unfindable.

export default {
  legalTitle: 'Legal Notice & Privacy',
  legalBinding: 'This is a courtesy translation. The German version is the legally binding one.',

  legalMissing: (fields) => `This page has not been filled in yet. Before launch, ${fields} must be set through the environment variables IMPRINT_NAME, IMPRINT_ADDRESS, IMPRINT_EMAIL and IMPRINT_RESPONSIBLE.`,

  legalImprintHeading: 'Provider identification under § 5 DDG',
  legalOperator: 'Operator',
  legalAddress: 'Address',
  legalEmail: 'E-mail',
  legalResponsible: 'Responsible for content under § 18 (2) MStV',

  legalContentHeading: 'Responsibility for content',
  legalContent: 'Event data comes from the venues themselves. They enter their own programme and are responsible for it. We do not review entries in advance, but we do follow up on notices — see',
  legalContentLink: 'Report',

  legalPrivacyHeading: 'Privacy',
  legalPrivacyLede: 'In short: an e-mail address to sign in, what you saved, who you follow. No tracking, no advertising identifiers, no third parties in your browser.',

  legalStoreHeading: 'What we store',
  legalStoreEmail: 'Your e-mail address — it is the only way to sign you in.',
  legalStoreName: 'Optionally a display name.',
  legalStoreActivity: 'Saved dates and the venues you follow.',
  legalStoreIp: 'A hashed IP address and a coarse browser family, for 30 days, solely against abuse.',

  legalNotStoreHeading: 'What we do not store',
  legalNotStore: 'No real name, no address, no date of birth, no phone number, no payment details, no location, no full user agent, no referrer and no third-party identifier. There is no advertising profile because there is no purpose that would need one.',

  legalPasswordHeading: 'There are no passwords',
  legalPassword: 'Signing in works with a six-digit code by e-mail. We store no passwords, so no password list can be stolen. Codes are stored hashed, are valid for ten minutes, and only in the window that requested them.',

  legalSensitiveHeading: 'Saved dates are the most sensitive thing here',
  legalSensitive: 'A record of who goes where and when is a profile of interests. In a scene whose clubs are also queer spaces, political spaces and safer spaces, that can be particularly worth protecting. So saved dates are never shown per person — not even to the venue. A venue sees a number, never a list. There is no "your friends are going". Who you follow is visible to nobody but you.',

  legalSensitiveModeration: 'There is one exception, and it is a narrow one: a moderator can see the saved dates and followed accounts of a single account while investigating a report. That is only possible by deliberately opening that one account — never from a list, never sorted by it, never exported — and every one of those views is logged, with its purpose. After twelve months the rows are reduced to a count anyway.',

  legalStatsHeading: 'Statistics',
  legalStatsOn: 'We count page views with a self-hosted Umami instance — no cookies, no per-person identifier, IP addresses hashed and discarded. That is why there is no cookie banner here: there is nothing to consent to.',
  legalStatsOff: 'Statistics are switched off in this installation.',

  legalImagesHeading: 'Images',
  legalImages: 'Uploaded images are re-encoded on the server. The original metadata disappears with it — including the GPS coordinates a phone photo carries.',

  legalRetentionHeading: 'For how long',
  legalRetentionCodes: 'sign-in codes: 24 hours',
  legalRetentionIps: 'hashed IP addresses: 30 days',
  legalRetentionSessions: 'expired sessions: 30 days',
  legalRetentionAnonymous: 'accounts with no activity and no e-mail address: 12 months',
  legalRetentionSaves: 'saved dates older than 12 months are reduced to a count and the rows deleted',

  legalRightsHeading: 'Your rights',
  legalRightsBefore: 'Access (Art. 15) and erasure (Art. 17) are buttons, not a support ticket:',
  legalRightsLink: 'in your account',
  legalRightsAfter: '. Deleting removes the data from the database immediately. It disappears from the backups within seven days — that belongs to the truth, or the deletion promise would be false.',

  // ----------------------------------------------------------------- about
  aboutTitle: 'About tba.tips',
  aboutLede: 'An event calendar for the Rhineland, built around the venues that run the events.',
  aboutRewriting: 'This text is being rewritten.',
  aboutAddProgramme: 'Add your programme',

  // ---------------------------------------------------------------- report
  reportTitle: 'Report',
  reportHeading: 'Report something',
  reportLede: (days) => `Tell us what is wrong. We check every report by hand and usually answer within ${days} working days. You do not need an account.`,
  reportReasonLabel: 'What is this about?',
  reportDetailLabel: 'What exactly?',
  reportContactLabel: 'How can we reach you? (optional)',
  reportContactHint: 'Only needed if you want an answer — an erasure request, for instance. Without an account it is the only way back to you.',
  reportSubmit: 'Report',
  reportReasonMissing: 'Please pick a reason.',
  reportIllegalBefore: 'Illegal content can also be sent directly to the address given in the',
  reportIllegalLink: 'legal notice',
  reportIllegalAfter: '.',
  reportThanksTitle: 'Thank you',
  reportThanks: (days) => `We will look into it — usually within ${days} working days. If you left an address, we will get back to you with the outcome.`,

  // The licence page. The footer links here rather than straight at the licence
  // text, because that text does not answer the one question that matters:
  // what exactly the ODbL covers. "Data: ODbL" otherwise reads as permission to
  // republish the venues' own writing, which it is not.
  licenceTitle: 'Data and licence',
  licenceLede: 'The database of venues, series and occurrences is licensed under the Open Database License 1.0. Use it for anything, including commercially.',
  licenceRightsHeading: 'Three different rights, and only one of them is ODbL',
  licenceRightsIntro: 'This distinction is the whole reason this page exists, so it is stated before the terms rather than after them.',
  licenceFactsTerm: 'Facts are not owned by anyone',
  licenceFacts: 'That a night starts at 23:00 at a named address on a named date is a fact, and facts are not copyrightable in Germany or anywhere else. Nobody needs our permission for one.',
  licenceDatabaseTerm: 'The database is',
  licenceDatabase: 'The EU sui-generis database right (§ 87a UrhG, Directive 96/9/EC) protects the collection: the substantial investment in assembling, verifying and presenting it. That right is real, it is the one we respect in other people’s listings, and it is what the ODbL licenses here.',
  licenceTextsTerm: 'The descriptions venues wrote are theirs',
  licenceTexts: 'A club’s own text about its own night is an authored work belonging to that club, not to us and not to the database. It is published here with their permission and is not covered by this licence. Take the facts freely; do not assume you may republish somebody’s copy.',
  licenceCrawlerNote: 'The same line runs through our crawler in the other direction: it takes dates, times, places and names, never description text and never artwork.',
  licenceAttributionHeading: 'Attribution, concretely',
  licenceAttribution: 'Credit tba.tips with a link to https://tba.tips wherever the data is shown, in a place a reader would look. That is what the ODbL calls a notice, and it is the only thing this licence really asks for.',
  licenceShareAlikeHeading: 'If you modify the database',
  licenceShareAlike: 'If you publish a modified version of the database, publish it under the same licence. That is the share-alike clause and it is deliberate: without it a commercial aggregator could absorb the whole regional dataset and give nothing back.',
  licencePersonalHeading: 'Personal data is not in it',
  licencePersonal: 'Accounts, sessions, saves and follows are not part of the licensed database and are never published. What is under the ODbL here is the programme: who is putting on what, when and where.',
  licenceMapHeading: 'The map carries a different ODbL',
  licenceMap: 'A second ODbL notice sits under every map. It points the other way: it is the credit we owe OpenStreetMap and Protomaps for the map data, not a licence we grant. Do not confuse the two.',
  licenceFullText: 'Full licence text (ODbL 1.0)',
  licenceCodeNote: 'The source code of this site is separately licensed under AGPL-3.0-only. A licence over data and a licence over software are two different things.',

  reasonWrong: 'The details are wrong',
  reasonCancelled: 'It is not happening',
  reasonOffensive: 'Abusive or hateful',
  reasonIllegal: 'Illegal',
  reasonSpam: 'Spam, or not a real venue',
  reasonErasure: 'Take my name off this (erasure request)',
  reasonOther: 'Something else',
};
